import { Box, Typography, Chip, Tooltip } from "@mui/material";
import React from "react";
import { styled } from "@mui/material/styles";
import FlagIcon from "@mui/icons-material/Flag";
import { useGetUIDesignReportQuery } from "../../useQuery";
import { AuthedFileLink } from "../../components-new/common";

const TableScroll = styled(Box)(() => ({
  overflowX: "auto",
  overflowY: "auto",
  maxHeight: "60vh",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  "&::-webkit-scrollbar": { width: "6px", height: "6px" },
  "&::-webkit-scrollbar-track": { background: "#f3f4f6" },
  "&::-webkit-scrollbar-thumb": {
    background: "#d1d5db",
    borderRadius: "3px",
    "&:hover": { background: "#9ca3af" },
  },
}));

const baseTh = {
  padding: "12px 16px",
  fontWeight: 600,
  textAlign: "center",
  background: "#f9fafb",
  borderBottom: "1px solid #e5e7eb",
  borderRight: "1px solid #e5e7eb",
  minWidth: "140px",
  color: "#374151",
  fontSize: "12px",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const baseTd = {
  padding: "12px 16px",
  borderBottom: "1px solid #e5e7eb",
  borderRight: "1px solid #e5e7eb",
  textAlign: "center",
  fontSize: "13px",
  verticalAlign: "middle",
};

const Dash = () => (
  <Typography fontSize="12px" color="#d1d5db">
    —
  </Typography>
);

const DesignPrincipalTable = ({ tcrid, testStepId, principleLevel }) => {
  const { data, isLoading, isError, error } = useGetUIDesignReportQuery(tcrid, {
    enabled: !!tcrid,
    testStepId,
    principleLevel,
  });
  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py="60px">
        <Typography fontSize="13px" color="#6b7280">
          Loading…
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py="60px">
        <Typography fontSize="13px" color="#dc2626" fontWeight={500}>
          {error?.message || "Failed to load"}
        </Typography>
      </Box>
    );
  }

  if (!data?.rows?.length) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py="60px">
        <Typography fontSize="13px" color="#6b7280">
          No data available
        </Typography>
      </Box>
    );
  }

  const principles = data.principles ?? [];
  const rows = data.rows ?? [];

  return (
    <Box>
      <TableScroll>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...baseTh, textAlign: "left", minWidth: "80px" }}>
                <Box px="16px" py="12px">
                  Step #
                </Box>
              </th>
              <th style={baseTh}>Milestone</th>
              <th style={baseTh}>Screenshot 1</th>
              <th style={baseTh}>Screenshot 2</th>
              {principles.map((p, i) => (
                <th key={i} style={{ ...baseTh, maxWidth: "200px" }}>
                  <Box sx={{ whiteSpace: "normal", lineHeight: 1.4 }}>
                    {rows[0]?.results?.find((r) => r.principle === p)?.name ?? p}
                  </Box>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.testStepId}>
                <td style={{ ...baseTd, textAlign: "left" }}>
                  <Box px="16px" py="12px">
                    <Typography fontSize="13px" fontWeight={600} color="#111827">
                      Step {row.stepNumber}
                    </Typography>
                  </Box>
                </td>

                <td style={baseTd}>
                  {row.milestone ? (
                    <Box display="flex" alignItems="center" justifyContent="center" gap="4px">
                      <FlagIcon sx={{ fontSize: 16, color: "#15803d" }} />
                      <Typography fontSize="12px" fontWeight={600} color="#15803d">
                        {row.milestone}
                      </Typography>
                    </Box>
                  ) : (
                    <Dash />
                  )}
                </td>

                <td style={baseTd}>
                  {row.screenshot1 ? (
                    <AuthedFileLink
                      url={row.screenshot1}
                      filename={`step-${row.stepNumber}-baseline.png`}
                      underline="always"
                      fontSize="12px"
                    >
                      Click to View
                    </AuthedFileLink>
                  ) : (
                    <Dash />
                  )}
                </td>

                <td style={baseTd}>
                  {row.failureScreenshot ? (
                    <AuthedFileLink
                      url={row.failureScreenshot}
                      filename={`step-${row.stepNumber}-screenshot.png`}
                      underline="always"
                      fontSize="12px"
                    >
                      Click to View
                    </AuthedFileLink>
                  ) : (
                    <Dash />
                  )}
                </td>

                {principles.map((p, pIdx) => {
                  const result = row.results?.find((r) => r.principle === p);
                  const isPassed = result?.result === "PASS";
                  return (
                    <td key={pIdx} style={baseTd}>
                      <Tooltip
                        title={result?.description ?? ""}
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              maxWidth: 280,
                              fontSize: "12px",
                              lineHeight: 1.5,
                              padding: "8px 12px",
                            },
                          },
                        }}
                      >
                        <Chip
                          label={result?.result ?? "N/A"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "11px",
                            backgroundColor: isPassed ? "#f0fdf4" : result ? "#fef2f2" : "#f3f4f6",
                            color: isPassed ? "#15803d" : result ? "#dc2626" : "#6b7280",
                            border: `1px solid ${isPassed ? "#dcfce7" : result ? "#fecaca" : "#e5e7eb"}`,
                          }}
                        />
                      </Tooltip>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </Box>
  );
};

export default DesignPrincipalTable;
